// Advanced AI Engine - Real Trading Insights
// Provides actionable insights for Iron Condor vs Directional strategies

export class AdvancedAIEngine {
  constructor(data) {
    this.data = data;
    this.dayNames = ['ראשון', 'שני', 'שלישי', 'רביעי', 'חמישי', 'שישי', 'שבת'];
  }

  /**
   * Generate comprehensive trading insights
   */
  generateTradingInsights() {
    return {
      seasonality: this.analyzeSeasonalityAdvanced(),
      dayOfWeekPatterns: this.analyzeDayOfWeekPatterns(),
      strategyRecommendation: this.recommendStrategy(),
      probabilityRanges: this.calculateProbabilityRanges(),
      recentTrend: this.analyzeRecentTrend(),
      volatilityState: this.analyzeVolatilityState(),
      alerts: this.generateAlerts(),
    };
  }

  /**
   * Advanced seasonality with year breakdown
   */
  analyzeSeasonalityAdvanced() {
    const monthYearStats = {};
    const monthNames = ['ינואר', 'פברואר', 'מרץ', 'אפריל', 'מאי', 'יוני',
                        'יולי', 'אוגוסט', 'ספטמבר', 'אוקטובר', 'נובמבר', 'דצמבר'];

    // Group by month and year
    this.data.forEach((exp, index) => {
      if (index === this.data.length - 1) return;
      
      const date = new Date(exp.date);
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();
      const key = `${month} ${year}`;

      const change = ((exp.price - this.data[index + 1].price) / this.data[index + 1].price) * 100;

      if (!monthYearStats[key]) {
        monthYearStats[key] = { changes: [], month, year };
      }
      monthYearStats[key].changes.push(change);
    });

    // Calculate stats per month-year
    const insights = [];
    Object.entries(monthYearStats).forEach(([key, data]) => {
      const avgChange = data.changes.reduce((a, b) => a + b, 0) / data.changes.length;
      const positiveCount = data.changes.filter(c => c > 0).length;
      const winRate = (positiveCount / data.changes.length) * 100;

      insights.push({
        period: key,
        month: data.month,
        year: data.year,
        avgChange: avgChange.toFixed(2),
        winRate: winRate.toFixed(0),
        trend: avgChange > 0.5 ? 'עולה' : avgChange < -0.5 ? 'יורד' : 'צדדי',
        significance: Math.abs(avgChange) > 1 ? 'גבוהה' : 'רגילה',
      });
    });

    // Find patterns (e.g., January is often negative)
    const monthlyAggregated = {};
    monthNames.forEach(month => {
      const monthData = insights.filter(i => i.month === month);
      if (monthData.length > 0) {
        const avgOfMonth = monthData.reduce((sum, i) => sum + parseFloat(i.avgChange), 0) / monthData.length;
        const negativeYears = monthData.filter(i => parseFloat(i.avgChange) < 0);
        
        monthlyAggregated[month] = {
          avgChange: avgOfMonth.toFixed(2),
          negativeYears: negativeYears.map(i => i.year),
          positiveYears: monthData.filter(i => parseFloat(i.avgChange) > 0).map(i => i.year),
          pattern: negativeYears.length > monthData.length / 2 ? 'בדרך כלל שלילי' : 'מעורב',
        };
      }
    });

    return {
      byMonthYear: insights.sort((a, b) => b.year - a.year || monthNames.indexOf(b.month) - monthNames.indexOf(a.month)),
      monthlyPatterns: monthlyAggregated,
    };
  }

  /**
   * Analyze patterns by day of week (Sunday vs Sunday, etc.)
   */
  analyzeDayOfWeekPatterns() {
    const dayPatterns = {
      0: { name: 'ראשון', weekly: [], data: [] },      // Sunday
      2: { name: 'שלישי', weekly: [], data: [] },      // Tuesday
      4: { name: 'חמישי שבועי', weekly: [], data: [] },  // Thursday weekly
      4.5: { name: 'חמישי חודשי', monthly: [], data: [] }, // Thursday monthly
    };

    // Group by day of week
    this.data.forEach((exp, index) => {
      const dayOfWeek = exp.dayOfWeek;
      const isMonthly = exp.type === 'חודשי';

      if (dayOfWeek === 0 || dayOfWeek === 2 || dayOfWeek === 4) {
        // Find previous same day
        let prevIndex = -1;
        for (let i = index + 1; i < this.data.length; i++) {
          if (isMonthly && dayOfWeek === 4) {
            // Monthly Thursday vs Monthly Thursday
            if (this.data[i].dayOfWeek === 4 && this.data[i].type === 'חודשי') {
              prevIndex = i;
              break;
            }
          } else if (!isMonthly && dayOfWeek === 4) {
            // Weekly Thursday vs Weekly Thursday
            if (this.data[i].dayOfWeek === 4 && this.data[i].type === 'שבועי') {
              prevIndex = i;
              break;
            }
          } else {
            // Sunday vs Sunday, Tuesday vs Tuesday
            if (this.data[i].dayOfWeek === dayOfWeek) {
              prevIndex = i;
              break;
            }
          }
        }

        if (prevIndex !== -1) {
          const change = ((exp.price - this.data[prevIndex].price) / this.data[prevIndex].price) * 100;
          const key = isMonthly && dayOfWeek === 4 ? 4.5 : dayOfWeek;

          if (dayPatterns[key]) {
            dayPatterns[key].data.push({
              date: exp.date,
              prevDate: this.data[prevIndex].date,
              change: change.toFixed(2),
              price: exp.price,
              prevPrice: this.data[prevIndex].price,
            });
          }
        }
      }
    });

    // Calculate statistics for each day
    const results = {};
    Object.entries(dayPatterns).forEach(([key, pattern]) => {
      if (pattern.data.length > 0) {
        const changes = pattern.data.map(d => parseFloat(d.change));
        const avgChange = changes.reduce((a, b) => a + b, 0) / changes.length;
        const positiveCount = changes.filter(c => c > 0).length;
        const negativeCount = changes.filter(c => c < 0).length;
        const maxChange = Math.max(...changes);
        const minChange = Math.min(...changes);
        
        const stdDev = Math.sqrt(
          changes.reduce((sum, c) => sum + Math.pow(c - avgChange, 2), 0) / changes.length
        );

        results[pattern.name] = {
          avgChange: avgChange.toFixed(2),
          stdDev: stdDev.toFixed(2),
          positiveRate: ((positiveCount / changes.length) * 100).toFixed(1),
          maxChange: maxChange.toFixed(2),
          minChange: minChange.toFixed(2),
          count: changes.length,
          recentTrend: changes.slice(0, 5).reduce((a, b) => a + b, 0) / 5,
          recommendation: this.getDayRecommendation(avgChange, stdDev),
        };
      }
    });

    return results;
  }

  getDayRecommendation(avgChange, stdDev) {
    if (stdDev < 1.5 && Math.abs(avgChange) < 0.5) {
      return 'מתאים ל-Iron Condor - תנודתיות נמוכה';
    } else if (stdDev > 3 || Math.abs(avgChange) > 2) {
      return 'שים לב! תנודתיות גבוהה - שקול Directional';
    } else if (avgChange > 1) {
      return 'מגמה עולה - שקול Bull Spread';
    } else if (avgChange < -1) {
      return 'מגמה יורדת - שקול Bear Spread';
    }
    return 'מעורב - המתן לסיגנל ברור יותר';
  }

  /**
   * Strategy recommendation based on current conditions
   */
  recommendStrategy() {
    const recent = this.data.slice(0, 10);
    
    // Calculate recent volatility
    const recentChanges = [];
    for (let i = 0; i < recent.length - 1; i++) {
      const change = Math.abs((recent[i].price - recent[i + 1].price) / recent[i + 1].price * 100);
      recentChanges.push(change);
    }
    
    const avgVolatility = recentChanges.reduce((a, b) => a + b, 0) / recentChanges.length;
    
    // Calculate trend
    const trendChanges = [];
    for (let i = 0; i < Math.min(5, recent.length - 1); i++) {
      const change = ((recent[i].price - recent[i + 1].price) / recent[i + 1].price) * 100;
      trendChanges.push(change);
    }
    const avgTrend = trendChanges.reduce((a, b) => a + b, 0) / trendChanges.length;

    // Check if next is monthly
    const isNextMonthly = recent[0].type === 'חודשי';

    let recommendation = {
      primary: '',
      reason: '',
      strikes: {},
      confidence: '',
      category: '', // 'שחיקה' or 'כיוון'
    };

    const currentPrice = recent[0].price;

    // Low volatility + neutral trend = Iron Condor (שחיקה)
    if (avgVolatility < 1.5 && Math.abs(avgTrend) < 0.5 && !isNextMonthly) {
      recommendation = {
        primary: 'Iron Condor',
        category: 'שחיקה (Theta Decay)',
        reason: `תנודתיות נמוכה (${avgVolatility.toFixed(2)}%) ומגמה צדדית - מושלם למכירת אופציות`,
        strikes: {
          putSell: Math.round(currentPrice * 0.97 / 10) * 10,
          putBuy: Math.round(currentPrice * 0.95 / 10) * 10,
          callSell: Math.round(currentPrice * 1.03 / 10) * 10,
          callBuy: Math.round(currentPrice * 1.05 / 10) * 10,
        },
        confidence: 'גבוהה',
        expectedWinRate: '65-70%',
      };
    }
    // High volatility + monthly expiration = Wait or Straddle
    else if (avgVolatility > 2.5 || isNextMonthly) {
      recommendation = {
        primary: 'המתן או Long Straddle',
        category: 'כיוון (Directional)',
        reason: `${isNextMonthly ? 'פקיעה חודשית + ' : ''}תנודתיות גבוהה (${avgVolatility.toFixed(2)}%) - צפוי מהלך חד`,
        strikes: {
          strike: Math.round(currentPrice / 10) * 10,
        },
        confidence: 'בינונית',
        expectedWinRate: '40-50%',
        warning: isNextMonthly ? '⚠️ פקיעה חודשית - סיכון גבוה יותר!' : '',
      };
    }
    // Strong uptrend = Bull Call Spread
    else if (avgTrend > 1) {
      recommendation = {
        primary: 'Bull Call Spread',
        category: 'כיוון (Directional)',
        reason: `מגמה עולה חזקה (${avgTrend.toFixed(2)}%) - כדאי להרוויח מהמשך העלייה`,
        strikes: {
          callBuy: Math.round(currentPrice * 1.00 / 10) * 10,
          callSell: Math.round(currentPrice * 1.05 / 10) * 10,
        },
        confidence: 'בינונית-גבוהה',
        expectedWinRate: '55-60%',
      };
    }
    // Strong downtrend = Bear Put Spread
    else if (avgTrend < -1) {
      recommendation = {
        primary: 'Bear Put Spread',
        category: 'כיוון (Directional)',
        reason: `מגמה יורדת חזקה (${avgTrend.toFixed(2)}%) - כדאי להרוויח מהמשך הירידה`,
        strikes: {
          putBuy: Math.round(currentPrice * 1.00 / 10) * 10,
          putSell: Math.round(currentPrice * 0.95 / 10) * 10,
        },
        confidence: 'בינונית-גבוהה',
        expectedWinRate: '55-60%',
      };
    }
    // Medium volatility = smaller Iron Condor
    else {
      recommendation = {
        primary: 'Iron Condor צר',
        category: 'שחיקה (Theta Decay)',
        reason: `תנודתיות בינונית (${avgVolatility.toFixed(2)}%) - Iron Condor עם טווח צר יותר`,
        strikes: {
          putSell: Math.round(currentPrice * 0.98 / 10) * 10,
          putBuy: Math.round(currentPrice * 0.96 / 10) * 10,
          callSell: Math.round(currentPrice * 1.02 / 10) * 10,
          callBuy: Math.round(currentPrice * 1.04 / 10) * 10,
        },
        confidence: 'בינונית',
        expectedWinRate: '60-65%',
      };
    }

    return recommendation;
  }

  /**
   * Calculate probability ranges for different movements
   */
  calculateProbabilityRanges() {
    const ranges = [
      { range: '±1%', threshold: 1 },
      { range: '±2%', threshold: 2 },
      { range: '±3%', threshold: 3 },
      { range: '±4%', threshold: 4 },
      { range: '±5%', threshold: 5 },
    ];

    const results = ranges.map(r => {
      let count = 0;
      for (let i = 0; i < this.data.length - 1; i++) {
        const change = Math.abs((this.data[i].price - this.data[i + 1].price) / this.data[i + 1].price * 100);
        if (change > r.threshold) count++;
      }
      
      const probability = (count / (this.data.length - 1)) * 100;

      return {
        range: r.range,
        probability: probability.toFixed(1),
        count,
        total: this.data.length - 1,
        ironCondorSuitable: probability < 30 ? 'מצוין' : probability < 40 ? 'טוב' : 'סיכון גבוה',
      };
    });

    return results;
  }

  /**
   * Analyze recent trend (last 5 expirations)
   */
  analyzeRecentTrend() {
    const recent = this.data.slice(0, 6);
    const changes = [];

    for (let i = 0; i < recent.length - 1; i++) {
      const change = ((recent[i].price - recent[i + 1].price) / recent[i + 1].price) * 100;
      changes.push(change);
    }

    const avgChange = changes.reduce((a, b) => a + b, 0) / changes.length;
    const positiveCount = changes.filter(c => c > 0).length;

    return {
      direction: avgChange > 0.5 ? 'עולה' : avgChange < -0.5 ? 'יורד' : 'צדדי',
      avgChange: avgChange.toFixed(2),
      consistency: positiveCount === changes.length || positiveCount === 0 ? 'עקבי' : 'מעורב',
      strength: Math.abs(avgChange) > 2 ? 'חזק' : Math.abs(avgChange) > 1 ? 'בינוני' : 'חלש',
    };
  }

  /**
   * Analyze current volatility state
   */
  analyzeVolatilityState() {
    const recent = this.data.slice(0, 20);
    const changes = [];

    for (let i = 0; i < recent.length - 1; i++) {
      const change = Math.abs((recent[i].price - recent[i + 1].price) / recent[i + 1].price * 100);
      changes.push(change);
    }

    const avgVol = changes.reduce((a, b) => a + b, 0) / changes.length;
    const stdDev = Math.sqrt(
      changes.reduce((sum, c) => sum + Math.pow(c - avgVol, 2), 0) / changes.length
    );

    // Calculate historical average
    const allChanges = [];
    for (let i = 0; i < this.data.length - 1; i++) {
      const change = Math.abs((this.data[i].price - this.data[i + 1].price) / this.data[i + 1].price * 100);
      allChanges.push(change);
    }
    const historicalAvg = allChanges.reduce((a, b) => a + b, 0) / allChanges.length;

    return {
      current: avgVol.toFixed(2),
      historical: historicalAvg.toFixed(2),
      state: avgVol < historicalAvg * 0.8 ? 'נמוכה' : avgVol > historicalAvg * 1.2 ? 'גבוהה' : 'רגילה',
      recommendation: avgVol < historicalAvg * 0.8 
        ? 'מצוין למכירת אופציות (Iron Condor)'
        : avgVol > historicalAvg * 1.2
        ? 'היזהר! תנודתיות גבוהה - שקול המתנה'
        : 'תנודתיות רגילה - ניתן לסחור',
    };
  }

  /**
   * Generate trading alerts
   */
  generateAlerts() {
    const alerts = [];
    const nextExp = this.data[0];

    // Monthly expiration alert
    if (nextExp.type === 'חודשי') {
      alerts.push({
        level: 'warning',
        icon: '⚠️',
        message: 'פקיעה חודשית הבאה - צפוי תנודתיות גבוהה יותר',
        action: 'שקול טווחים רחבים יותר או המתן',
      });
    }

    // Check if today is specific day pattern
    const dayOfWeek = nextExp.dayOfWeek;
    const dayName = this.dayNames[dayOfWeek];
    
    if (dayOfWeek === 0) { // Sunday
      alerts.push({
        level: 'info',
        icon: 'ℹ️',
        message: `פקיעת ${dayName} - בדוק דפוס ${dayName} vs ${dayName}`,
        action: 'לחץ על טאב "דפוסים" לפרטים',
      });
    }

    // Volatility alert
    const volState = this.analyzeVolatilityState();
    if (volState.state === 'גבוהה') {
      alerts.push({
        level: 'danger',
        icon: '🔴',
        message: `תנודתיות גבוהה (${volState.current}% vs ממוצע ${volState.historical}%)`,
        action: 'שקול המתנה או Directional strategy',
      });
    } else if (volState.state === 'נמוכה') {
      alerts.push({
        level: 'success',
        icon: '🟢',
        message: `תנודתיות נמוכה (${volState.current}% vs ממוצע ${volState.historical}%)`,
        action: 'מצוין ל-Iron Condor!',
      });
    }

    // Trend alert
    const trend = this.analyzeRecentTrend();
    if (trend.strength === 'חזק') {
      alerts.push({
        level: 'info',
        icon: '📈',
        message: `מגמה ${trend.direction} חזקה בשבועות האחרונים (${trend.avgChange}%)`,
        action: trend.direction === 'עולה' ? 'שקול Bull Spread' : 'שקול Bear Spread',
      });
    }

    return alerts;
  }
}

export default AdvancedAIEngine;
